/**
 * SupportSessionDetailsDrawer.tsx
 * ----------------------------------------------------------------------------
 * Drawer (Sheet lateral) com o detalhe completo de UMA sessão de suporte WL,
 * incluindo a lista de ações registradas em wl_audit_log durante a janela.
 *
 * Suporta dois "escopos" via prop `scope`:
 *   - "wl"    → usa useWlSupportSessionDetails (RPC wl_get_support_session_details)
 *   - "admin" → usa useAdminSupportSessionDetails (RPC admin_get_support_session_details)
 *
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, ShieldAlert, KeyRound, Building2, User, Clock } from "lucide-react";
import {
  useWlSupportSessionDetails,
  useAdminSupportSessionDetails,
} from "@/hooks/wl/useSupportSessionsAudit";
import { fmtDt, fmtDuration, STATUS_LABEL, STATUS_TONE } from "./SupportSessionsTable";
import { cn } from "@/lib/utils";

interface SupportSessionDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string | null;
  scope: "wl" | "admin";
}

export function SupportSessionDetailsDrawer({
  open,
  onOpenChange,
  sessionId,
  scope,
}: SupportSessionDetailsDrawerProps) {
  // Seleciona o hook correto conforme o escopo
  const wlQ = useWlSupportSessionDetails(scope === "wl" ? sessionId : null);
  const adminQ = useAdminSupportSessionDetails(scope === "admin" ? sessionId : null);
  const q = scope === "wl" ? wlQ : adminQ;
  const data = q.data;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-amber-500" />
            Detalhe da Sessão de Suporte
          </SheetTitle>
          <SheetDescription>
            {sessionId
              ? `ID: ${sessionId.substring(0, 8).toUpperCase()}`
              : "Selecione uma sessão"}
          </SheetDescription>
        </SheetHeader>

        {q.isLoading && (
          <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando…
          </div>
        )}

        {data && (
          <div className="space-y-4 mt-4">
            {/* Status + duração */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className={cn("h-6 text-xs font-medium", STATUS_TONE[data.status])}
              >
                {STATUS_LABEL[data.status]}
              </Badge>
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {fmtDuration(data.duration_seconds)}
              </span>
              {data.provisioned_member && (
                <Badge variant="secondary" className="h-5 text-[10px]">Provisionou membro</Badge>
              )}
              {data.provisioned_role && (
                <Badge variant="secondary" className="h-5 text-[10px]">Provisionou papel</Badge>
              )}
            </div>

            <Separator />

            {/* Operador + Tenant */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                  <User className="h-3 w-3" /> Operador
                </p>
                <p className="text-sm font-medium">{data.operator_display_name || "—"}</p>
                <p className="text-xs text-muted-foreground">{data.operator_email || "—"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> Tenant
                </p>
                <p className="text-sm font-medium">{data.tenant_name || "—"}</p>
                <p className="text-xs text-muted-foreground font-mono">{data.tenant_slug || "—"}</p>
              </div>
            </div>

            <Separator />

            {/* Motivo */}
            <div className="space-y-1">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                <ShieldAlert className="h-3 w-3 text-amber-500" /> Motivo
              </p>
              <p className="text-sm">{data.reason || "—"}</p>
            </div>

            <Separator />

            {/* Datas */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Início</p>
                <p className="text-foreground">{fmtDt(data.started_at)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Expira em</p>
                <p className="text-foreground">{fmtDt(data.expires_at)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Encerrada em</p>
                <p className="text-foreground">{fmtDt(data.ended_at)}</p>
              </div>
            </div>

            {data.ended_reason && (
              <p className="text-xs text-muted-foreground">
                Encerramento: <span className="font-medium text-foreground">{data.ended_reason}</span>
              </p>
            )}

            <Separator />

            {/* Ações executadas */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                Ações executadas
                <Badge variant="outline" className="h-5 text-[10px]">
                  {data.actions?.length ?? 0}
                </Badge>
              </h4>

              {(!data.actions || data.actions.length === 0) && (
                <p className="text-xs text-muted-foreground italic">
                  Nenhuma ação registrada em wl_audit_log durante esta sessão.
                </p>
              )}

              {data.actions && data.actions.length > 0 && (
                <ScrollArea className="max-h-[360px] rounded-md border">
                  <div className="divide-y">
                    {data.actions.map((a) => (
                      <div key={a.id} className="px-3 py-2 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono font-medium text-foreground truncate">
                            {a.action}
                          </span>
                          <span className="text-muted-foreground whitespace-nowrap">
                            {format(new Date(a.created_at), "dd/MM HH:mm:ss", { locale: ptBR })}
                          </span>
                        </div>
                        <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                          {a.menu_key && <span>menu: {a.menu_key}</span>}
                          {a.target_entity && (
                            <span>
                              {a.target_entity}
                              {a.target_entity_id
                                ? ` · ${a.target_entity_id.substring(0, 8).toUpperCase()}`
                                : ""}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
